/* eslint-disable no-alert */
/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'

import PageContext from '@contexts/pageContext'
import { useModals, useSetState } from '@campaignhub/react-hooks'
import { useParams } from 'react-router-dom'

import {
  Box, Columns, DashboardModule, LoadingModule, ModalContext,
} from '@campaignhub/suit-theme'

import blankState from '@components/assets/blankState.svg'
import serviceJobNoteTypes from '@functions/serviceJobNoteTypes'

import useCurrentUser from '@hooks/useCurrentUser'
import useReduxAction from '@hooks/useReduxAction'
import useServiceJob from '@hooks/useServiceJob'
import useUploadResource from '@hooks/useUploadResource'
import useUploadAsset from '@hooks/useUploadAsset'
import useResourceTypes from '@hooks/useResourceTypes'

import ChangeJobAssignmentModal from '@modals/ChangeJobAssignmentModal'
import ClientResourceModal from '@modals/ClientResourceModal'
import EditServiceJobDetailsModal from '@modals/EditServiceJobDetailsModal'
import EditResourceDetailModal from '@modals/EditResourceDetailModal'
import UploadModal from '@modals/UploadModal'
import UploadResourceModal from '@modals/UploadResourceModal'

import GalleryOverlay from '@components/GalleryOverlay'
import ServiceJobNote from '@components/ServiceJobNote'
import custom from '@styles/custom.module.scss'
import ActivityLogs from './components/ActivityLogs'
import AppointmentDetails from './components/AppointmentDetails'
import AssetsAndResources from './components/AssetsAndResources'
import AssignedUsers from './components/AssignedUsers'
import AvailableResources from './components/AvailableResources'
import ClientProductionNotes from './components/ClientProductionNotes'
import DuskPositional from './components/DuskPositional'
import JobDetails from './components/JobDetails'
import JobHeader from './components/JobHeader'
import JobSummary from './components/JobSummary'
import ListingDetails from './components/ListingDetails'
import ProductDetails from './components/ProductDetails'
import PostProductionChecklist from './components/PostProductionChecklist'
import ProductionNotes from './components/ProductionNotes'
import ProviderChecklist from './components/ProviderChecklist'
import ChecklistItems from './components/ChecklistItems'
import StudioFeedback from './components/StudioFeedback'
import StripeRefund from './components/StripeRefund'

const assignServiceJobStatus = (serviceJobParam, createFn, updateWorkflow) => {
  createFn(serviceJobParam).then(({ errors, success }) => {
    if (!success && errors) {
      toast.warning(errors[0])
      return
    }
    updateWorkflow(serviceJobParam.serviceJobStatusId)
  })
}

const assignServiceJobUser = (serviceJobUserParams, additionalParams, createFn, createFnServiceJobStatus, updateWorkflow, setState) => {
  createFn(serviceJobUserParams).then(({ success, errors }) => {
    if (!success) {
      toast.warning(errors[0])
      return
    }

    if (success) {
      if (additionalParams.toggleToAssigned) {
        createFnServiceJobStatus({
          id: serviceJobUserParams.serviceJobId,
          serviceJobStatusId: additionalParams.assignedStatusId,
          workflowComment: 'Assigned',
        }).then(({ errorData, successData }) => {
          if (!successData && errorData) {
            toast.warning(errorData[0])
            return
          }
          updateWorkflow(additionalParams.assignedStatusId)
        })
      }
    }

    setState({ showChangeJobAssignmentModal: false })
  })
}

const updateServiceJobUser = (serviceJobUser, updateFn, setState) => {
  updateFn(serviceJobUser).then(({ success, errors }) => {
    if (!success && errors) {
      toast.warning(errors[0])
      return
    }

    setState({ showChangeJobAssignmentModal: false })
  })
}

const updateResource = (resource, updateFn, setState) => {
  updateFn(resource).then(({ success, errors }) => {
    if (!success && errors) {
      toast.warning(errors[0])
      return
    }

    setState({ showEditResourceDetailModal: false })
  })
}

const callbacks = (component, props, setState, progressRef, uploadsRef, callback, createFnServiceJobStatus) => {
  const progress = progressRef
  const uploads = uploadsRef
  const componentCallbacks = {
    ChangeJobAssignmentModal: {
      closeModal: () => setState({ showChangeJobAssignmentModal: false }),
      assignServiceJobUser: (serviceJobUserParams, additionalParams, createFn) => assignServiceJobUser(
        serviceJobUserParams,
        additionalParams,
        createFn,
        createFnServiceJobStatus,
        callback,
        setState,
      ),
      updateServiceJobUser: (serviceJobUser, updateFn) => updateServiceJobUser(serviceJobUser, updateFn, setState),
    },
    ClientResourceModal: {
      closeModal: () => setState({ showClientResourceModal: false }),
    },
    EditServiceJobDetailsModal: {
      closeModal: () => setState({ showEditServiceJobDetailsModal: false }),
    },
    EditResourceDetailModal: {
      closeModal: () => setState({ showEditResourceDetailModal: false }),
      updateResource: (resource, updateFn) => updateResource(resource, updateFn, setState),
    },
    ResourceUploadModal: {
      checkProgress: (progressStatus) => {
        progress.current = progressStatus
      },
      checkUploadStatus: (uploadStatus) => {
        uploads.current = uploadStatus
      },
      closeModal: () => {
        if ((progress.current && progress.current !== 0 && uploads.current.length === 0) || (progress.current === 0 && uploads.current.length === 0)) {
          if (window.confirm('This action will close the modal and interrupt the ongoing upload.\nDo you still wish to close?')) {
            progress.current = undefined
            uploads.current = []
            setState({ showResourceUploadModal: false })
          }
        } else {
          progress.current = undefined
          uploads.current = []
          setState({ showResourceUploadModal: false })
        }
      },
    },
    AssetUploadModal: {
      checkProgress: (progressStatus) => {
        progress.current = progressStatus
      },
      checkUploadStatus: (uploadStatus) => {
        uploads.current = uploadStatus
      },
      closeModal: () => {
        if ((progress.current && progress.current !== 0 && uploads.current.length === 0) || (progress.current === 0 && uploads.current.length === 0)) {
          if (window.confirm('This action will close the modal and interrupt the ongoing upload.\nDo you still wish to close?')) {
            progress.current = undefined
            uploads.current = []
            setState({ showAssetUploadModal: false })
          }
        } else {
          progress.current = undefined
          uploads.current = []
          setState({ showAssetUploadModal: false })
        }
      },
      assignServiceJobStatus: (serviceJobParams, createFn) => assignServiceJobStatus(serviceJobParams, createFn, callback),
    },
    MultipleResourceUploadModal: {
      checkProgress: (progressStatus) => {
        progress.current = progressStatus
      },
      checkUploadStatus: (uploadStatus) => {
        uploads.current = uploadStatus
      },
      closeModal: () => {
        if ((progress.current && progress.current !== 0 && uploads.current.length === 0) || (progress.current === 0 && uploads.current.length === 0)) {
          if (window.confirm('This action will close the modal and interrupt the ongoing upload.\nDo you still wish to close?')) {
            progress.current = undefined
            uploads.current = []
            setState({ showMultipleResourceUploadModal: false })
          }
        } else {
          progress.current = undefined
          uploads.current = []
          setState({ showMultipleResourceUploadModal: false })
        }
      },
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  allowedResourceFileTypes: 'image/*',
  disableResourceUpload: false,
  duskContent: '',
  duskTotalImage: 0,
  initialAssetId: null,
  loaded: false,
  refreshClientDetails: 0,
  showAssetUploadModal: false,
  showChangeJobAssignmentModal: false,
  showClientResourceModal: false,
  showEditResourceDetailModal: false,
  showEditServiceJobDetailsModal: false,
  showGalleryOverlay: false,
  showMultipleResourceUploadModal: false,
  showResourceUploadModal: false,
}

const ServiceJob = (props) => {
  const { userType } = props

  const [state, setState] = useSetState(defaultState)
  const {
    allowedResourceFileTypes,
    disableResourceUpload,
    duskContent,
    duskTotalImage,
    initialAssetId,
    refreshClientDetails,
    showAssetUploadModal,
    showChangeJobAssignmentModal,
    showClientResourceModal,
    showEditResourceDetailModal,
    showEditServiceJobDetailsModal,
    showGalleryOverlay,
    showMultipleResourceUploadModal,
    showResourceUploadModal,
    workflowUpdated,
  } = state

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const { id: serviceJobId } = useParams()
  const { currentUser } = useCurrentUser()

  const includes = ['campaign', 'client', 'contentDirectProviders', 'details', 'service', 'users', 'notes']
  useReduxAction('serviceJobs', 'loadServiceJobV1', {
    includes: includes.join(','),
  }, [serviceJobId], {
    dispatchAction: (action, requestOptions) => action(serviceJobId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { loadedIds } = entityReducer
      return serviceJobId && !loadedIds.includes(serviceJobId)
    },
  })

  const updateDuskDetail = (content, totalImage) => {
    setState({ duskContent: content, duskTotalImage: totalImage })
  }

  const {
    callbacks: {
      assignServiceJobStatus: createFnServiceJobStatus,
    },
    filteredServiceResourceTypes,
    loading: serviceJobLoading,
    errors: serviceJobErrors,
    serviceJob,
    showDuskPositional,
  } = useServiceJob({ id: serviceJobId })
  const modalRequestOptions = { serviceJobId: serviceJob.id }

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceGroups, serviceJobDetails, serviceJobs, serviceJobClientDetails, serviceJobChecklistItemTypes } = entities

  useReduxAction('serviceJobTags', 'loadServiceJobTags', { serviceJobs: serviceJobId }, [serviceJob?.name], {
    shouldPerformFn: (entityReducer) => {
      const { loading, errors } = entityReducer
      return !loading && serviceJob?.name && !errors?.length
    },
  })

  useReduxAction('serviceJobClientDetails', 'loadServiceJobClientDetail', {}, [serviceJob?.name, refreshClientDetails], {
    dispatchAction: (action, requestOptions) => action(serviceJobId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { loading } = entityReducer
      return !loading && serviceJob?.name && !serviceJobClientDetails[serviceJobId]
    },
  })

  useReduxAction('serviceJobHistories', 'loadServiceJobHistories', {}, [serviceJob?.serviceJobStatusId], {
    dispatchAction: (action, requestOptions) => action(serviceJobId, requestOptions),
    shouldPerformFn: (entityReducer) => {
      const { loading, errors } = entityReducer
      return !loading && serviceJob?.serviceJobStatusId === 180 && !errors?.length
    },
  })

  const updateWorkflow = (statusId) => {
    serviceJob.serviceJobStatusId = statusId
    setState({ workflowUpdated: !workflowUpdated })
  }

  const useUploadResourcePayload = useUploadResource()
  const {
    callbacks: {
      deleteResource: deleteFnResource,
      createResourceDirect: createFnResourceDirect,
      scanFolder: scanFnFolder,
    },
  } = useUploadResourcePayload

  const useUploadAssetPayload = useUploadAsset()
  const {
    callbacks: {
      deleteAsset: deleteFnAsset,
      createAssetDirect: createFnAssetDirect,
    },
  } = useUploadAssetPayload

  const pageContext = {
    callbacks: {
      showChangeJobAssignmentModal: (payload) => {
        setModalData('ChangeJobAssignmentModal', payload)
        setState({ showChangeJobAssignmentModal: true })
      },
      showClientResourceModal: (payload) => {
        setModalData('ClientResourceModal', payload)
        setState({ showClientResourceModal: true })
      },
      showEditServiceJobDetailsModal: (payload) => {
        setModalData('EditServiceJobDetailsModal', payload)
        setState({ showEditServiceJobDetailsModal: true })
      },
      showEditResourceDetailModal: (payload) => {
        setModalData('EditResourceDetailModal', payload)
        setState({ showEditResourceDetailModal: true })
      },
      showResourceUploadModal: (payload) => {
        setModalData('showResourceUploadModal', payload)
        setState({ showResourceUploadModal: true })
      },
      showAssetUploadModal: (payload) => {
        setModalData('showAssetUploadModal', payload)
        setState({ showAssetUploadModal: true })
      },
      showMultipleResourceUploadModal: (payload) => {
        setModalData('showMultipleResourceUploadModal', payload)
        setState({ showMultipleResourceUploadModal: true })
      },
      assignServiceJobStatus: (serviceJobUserParams, createFn) => {
        assignServiceJobStatus(serviceJobUserParams, createFn, updateWorkflow)
      },
      refreshServiceJobClientDetails: () => setState({ refreshClientDetails: refreshClientDetails + 1 }),
      toggleUpdateDuskDetail: (content, totalImage) => updateDuskDetail(content, totalImage),
      toggleGalleryOverlay: (assetId) => setState({ showGalleryOverlay: !showGalleryOverlay, initialAssetId: assetId }),
      toggleSetState: (newState) => setState(newState),
    },
    currentUser,
    duskContent,
    duskTotalImage,
    initialAssetId,
    serviceJob,
    serviceJobs,
    showDuskPositional,
    workflowUpdated,
  }

  useEffect(() => {
    setState({ allowedResourceFileTypes: serviceGroups[serviceJob.serviceGroupId]?.allowedResourceFileTypes })
  }, [serviceJob.serviceGroupId, serviceGroups])

  useResourceTypes()

  const campaignPhotoJobs = Object.values(serviceJobs)?.filter(x => x?.campaign?.id === serviceJob?.campaign?.id && ['Photography'].includes(serviceGroups[x?.serviceGroupId]?.name))
  const duskPositionalDetails = Object.values(serviceJobDetails).find(x => campaignPhotoJobs?.find(y => y?.details?.includes(x.id) && x.entityFieldType?.name === 'DuskPositional'))

  const details = Object.values(serviceJobDetails).filter(x => serviceJob?.details?.includes(x.id))
  const providerChecklist = details.filter(x => x.entityFieldType.name === 'ProviderChecklist')
  const productionNotes = details.filter(x => x.entityFieldType.name === 'ClientStudioNote')
  const clientProductionNotes = details.filter(x => x.entityFieldType.name === 'ProductionNote')
  const contentExpressDetails = details.find(x => x.entityFieldType.name === 'ContentExpressDetails') || null

  useEffect(() => {
    setState({
      disableResourceUpload: providerChecklist.some(x => {
        const parsedValue = JSON.parse(x.value);
        return !parsedValue.checked && parsedValue.enforce;
      })
    })
  }, [serviceJobDetails])

  const progressRef = useRef()
  const uploadsRef = useRef()

  const resourceTypesOrder = ['DuskPositional', 'BracketDay', 'Flash', 'BracketDusk', 'Clones']
  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        {serviceJob?.name && !serviceJobLoading && (
          <>
            <JobHeader campaign={serviceJob.campaign} details={details} />
            <Box paddingX="large" paddingTop={[112, 105]}>
              <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
                <Columns.Main>
                  <AssetsAndResources
                    disableResourceUpload={['FullTimeProvider', 'ContractualProvider'].includes(currentUser.userRole?.name) ? disableResourceUpload : false}
                    hideProof={['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)}
                    resourceTypes={filteredServiceResourceTypes}
                    serviceJob={serviceJob}
                    showResourcesOnly={(userType === 'Onsite' && !['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name))
                      || !['Copywriting', 'Photography', 'Floorplan', 'Video', 'VidMo', 'Virtual Staging', 'Virtual Tour'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)}
                    workflowUpdated={workflowUpdated}
                  />
                  <JobSummary
                    campaign={serviceJob.campaign}
                    client={serviceJob.client}
                    details={details}
                    totalAssets={serviceJob.totalAssets}
                    serviceJobLoading={serviceJobLoading}
                    showJobLength={userType === 'Onsite'}
                  />
                  <PostProductionChecklist
                    serviceJobId={serviceJob.id}
                    hidden={userType === 'Onsite'}
                  />
                  <ClientProductionNotes
                    clientProductionNotes={clientProductionNotes.at(0) ? JSON.parse(clientProductionNotes.at(0).value).value : serviceJob.client?.productionNote}
                    hidden={userType !== 'Onsite'}
                  />
                  {!serviceJobLoading && (
                    <ProviderChecklist
                      providerChecklist={providerChecklist}
                      serviceJobChecklistItemTypes={serviceJobChecklistItemTypes}
                    />
                  )}
                  {(['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)
                    || (!['Copywriting'].includes(serviceGroups[serviceJob.serviceGroupId]?.name) && userType === 'Studio'))
                    && (
                      <ProductionNotes productionNotes={productionNotes} />
                    )}
                  <AvailableResources hidden />
                  <ServiceJobNote
                    dashboardModule
                    editable
                    title="Shoot List (Please put your shoot specifications here)"
                    serviceJobId={serviceJob.id}
                    serviceJobNoteTypeId={serviceJobNoteTypes.shootListNote.id}
                  />
                  <ChecklistItems
                    dashboardModule
                    editable
                    title="Provider Notes"
                    serviceJobId={serviceJob.id}
                    serviceJobChecklistItemTypeId={6}
                    showUpdatedStatus
                  />
                  <ServiceJobNote
                    dashboardModule
                    title="Client Booking Notes"
                    serviceJobId={serviceJob.id}
                    serviceJobNoteTypeId={serviceJobNoteTypes.clientBookingNote.id}
                  />
                  {(showDuskPositional || ['Dusk Photography'].includes(serviceJob?.service?.name)) && (
                    <DuskPositional serviceJobDetail={duskPositionalDetails?.value} serviceJobId={serviceJob.id} editable={showDuskPositional} />
                  )}
                  <StudioFeedback hidden />
                  <ListingDetails hidden client={serviceJob.client} />
                  <ProductDetails hidden service={serviceJob.service} details={details} />
                  <Box flexDirection="column" variant="white" className={custom.scroll}>
                    <ActivityLogs serviceJob={serviceJob} workflowUpdated={workflowUpdated} />
                  </Box>
                </Columns.Main>
                <Columns.Sidebar>
                  <JobDetails hidden={userType === 'Onsite'} />
                  {contentExpressDetails && (
                    <StripeRefund hidden={!contentExpressDetails?.value} canRefund={userType === 'BackOffice'} />
                  )}
                  <AssignedUsers hidden={userType === 'Onsite'} />
                  <AppointmentDetails dateService={serviceJob.dateService} details={details} />
                </Columns.Sidebar>
              </Columns>
            </Box>
          </>
        )}
        {serviceJobLoading ? (
          <Box padding="large">
            <LoadingModule loading={serviceJobLoading} />
          </Box>
        ) : serviceJobErrors?.length && !serviceJob?.name ? (
          <Box padding="large">
            <DashboardModule
              title="Service Job"
              headerRight={(
                <DashboardModule.Link href="/">
                  Back to Dashboard
                </DashboardModule.Link>
              )}
            >
              <DashboardModule.BlankState imageUrl={blankState}>
                <DashboardModule.BlankState.Title>{serviceJobErrors[0] || 'Not found'}</DashboardModule.BlankState.Title>
                <DashboardModule.BlankState.Paragraph>
                  This job is no longer available.
                  It may have been removed or updated.
                  Please use the search bar to find the latest link.
                </DashboardModule.BlankState.Paragraph>
              </DashboardModule.BlankState>
            </DashboardModule>
          </Box>
        ) : null}
        <EditServiceJobDetailsModal
          callbacks={callbacks('EditServiceJobDetailsModal', props, setState)}
          showModal={showEditServiceJobDetailsModal}
          serviceJob={serviceJob}
        />
        <ChangeJobAssignmentModal
          callbacks={callbacks('ChangeJobAssignmentModal', props, setState, progressRef, uploadsRef, updateWorkflow, createFnServiceJobStatus)}
          showModal={showChangeJobAssignmentModal}
          serviceJob={serviceJob}
        />
        <ClientResourceModal
          callbacks={callbacks('ClientResourceModal', props, setState)}
          showModal={showClientResourceModal}
        />
        <EditResourceDetailModal
          callbacks={callbacks('EditResourceDetailModal', props, setState)}
          showModal={showEditResourceDetailModal}
        />
        <UploadModal
          callbacks={{
            ...callbacks('ResourceUploadModal', props, setState, progressRef, uploadsRef, updateWorkflow),
            createDirectFn: createFnResourceDirect,
            scanFolderFn: scanFnFolder,
            deleteFn: deleteFnResource,
          }}
          serviceJob={serviceJob}
          serviceJobOptions={modalRequestOptions}
          showModal={showResourceUploadModal}
          title="Upload"
          titleSecondLine="Resources"
          type="Resources"
          resourceTypes={filteredServiceResourceTypes?.filter(x => !['BracketDay', 'BracketDusk', 'Clones', 'Flash'].includes(x.name))}
          fileTypesAllowed={allowedResourceFileTypes}
        />
        <UploadModal
          callbacks={{
            ...callbacks('AssetUploadModal', props, setState, progressRef, uploadsRef, updateWorkflow),
            createDirectFn: createFnAssetDirect,
            deleteFn: deleteFnAsset,
          }}
          serviceJob={serviceJob}
          serviceJobOptions={modalRequestOptions}
          showModal={showAssetUploadModal}
          title="Upload"
          titleSecondLine="Assets"
          type="Assets"
        />
        <UploadResourceModal
          callbacks={{
            ...callbacks('MultipleResourceUploadModal', props, setState, progressRef, uploadsRef),
            scanFolderFn: scanFnFolder,
            deleteFn: deleteFnResource,
          }}
          serviceJob={serviceJob}
          serviceJobOptions={modalRequestOptions}
          showModal={showMultipleResourceUploadModal}
          title="Upload"
          titleSecondLine="Resources"
          type="Resources"
          resourceTypes={filteredServiceResourceTypes
            ?.filter(x => x.name !== 'PhotographyImage')
            ?.sort((a, b) => resourceTypesOrder.indexOf(a.name) - resourceTypesOrder.indexOf(b.name))}
          fileTypesAllowed={allowedResourceFileTypes}
        />
        {showGalleryOverlay ? <GalleryOverlay isPhotographyJob={['Photography'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)} /> : null}
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

ServiceJob.propTypes = {
  userType: PropTypes.string,
}

export default ServiceJob
